import React, { useEffect, useState } from "react";
import { templatesApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Copy, Archive, ArchiveRestore, Trash2, Repeat, Play } from "lucide-react";
import { toast } from "sonner";
import TemplateEditor from "@/components/TemplateEditor";
import NewFromTemplateDialog from "@/components/NewFromTemplateDialog";

/**
 * Template Manager — list / create / edit / duplicate / archive / delete.
 * Archived templates are hidden by default from creation choices; a "Show
 * archived" toggle brings them back for management.
 */
export default function TemplatesPage() {
  const [items, setItems] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [editor, setEditor] = useState({ open: false, template: null });
  const [launch, setLaunch] = useState({ open: false, template: null });

  const load = async () => {
    const r = await templatesApi.list(showArchived);
    setItems(r.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const duplicate = async (t) => {
    try { await templatesApi.duplicate(t.id); toast.success("Duplicated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Duplicate failed"); }
  };
  const archive = async (t) => {
    try { await templatesApi.archive(t.id); toast.success("Archived"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Archive failed"); }
  };
  const unarchive = async (t) => {
    try { await templatesApi.unarchive(t.id); toast.success("Restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    try { await templatesApi.remove(t.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden max-w-full" data-testid="templates-page">
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-5xl mx-auto">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-eyebrow">Executive Overview</div>
            <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mt-1">Project Templates</h1>
            <p className="text-sm text-white/60 mt-2 max-w-2xl">
              Reusable blueprints for launching real projects in seconds. Editing a template never rewrites projects already created from it.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-white/60 px-3 py-2 rounded-md border border-white/10 bg-white/[0.03] cursor-pointer">
              <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} data-testid="show-archived-toggle"/>
              Show archived
            </label>
            <Button onClick={() => setEditor({ open: true, template: null })} data-testid="new-template-btn" className="rounded-full bg-white text-black hover:bg-white/90">
              <Plus className="w-4 h-4 mr-2"/> New template
            </Button>
          </div>
        </div>

        {items.length === 0 && (
          <div className="text-center py-16 border border-dashed border-white/10 rounded-xl" data-testid="templates-empty">
            <div className="text-sm text-white/50 mb-3">No templates yet. Build a reusable blueprint to spin up projects in seconds.</div>
            <Button onClick={() => setEditor({ open: true, template: null })} className="rounded-full bg-white text-black hover:bg-white/90">
              <Plus className="w-4 h-4 mr-2"/> Create your first template
            </Button>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          {items.map(t => (
            <div key={t.id} className={`p-5 rounded-xl border bg-[#151A23] ${t.archived ? "border-white/5 opacity-60" : "border-white/10"}`} data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between mb-2 gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-semibold text-lg break-words">{t.name}</h3>
                    {t.archived && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-white/15 text-white/50">Archived</span>}
                  </div>
                  {t.description && <p className="text-sm text-white/60 mt-1 line-clamp-2">{t.description}</p>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs text-white/60 mt-3">
                <div><div className="text-white/40 uppercase text-[10px] tracking-wider">Tasks</div><div className="text-white/90 mt-0.5">{(t.tasks || []).length}</div></div>
                <div><div className="text-white/40 uppercase text-[10px] tracking-wider">Milestones</div><div className="text-white/90 mt-0.5">{(t.events || []).length}</div></div>
                <div><div className="text-white/40 uppercase text-[10px] tracking-wider">Duration</div><div className="text-white/90 mt-0.5">{t.default_duration_days ? `${t.default_duration_days}d` : "—"}</div></div>
              </div>
              <div className="mt-4 pt-3 border-t border-white/10 flex flex-wrap items-center gap-2">
                {!t.archived && (
                  <Button size="sm" onClick={() => setLaunch({ open: true, template: t })} data-testid={`launch-template-${t.id}`} className="rounded-full bg-white text-black hover:bg-white/90 h-8">
                    <Play className="w-3.5 h-3.5 mr-1.5"/> New project
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setEditor({ open: true, template: t })} data-testid={`edit-template-${t.id}`} className="h-8 text-white/70 hover:text-white hover:bg-white/10">
                  <Pencil className="w-3.5 h-3.5 mr-1.5"/> Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => duplicate(t)} data-testid={`duplicate-template-${t.id}`} className="h-8 text-white/70 hover:text-white hover:bg-white/10">
                  <Copy className="w-3.5 h-3.5 mr-1.5"/> Duplicate
                </Button>
                {t.archived ? (
                  <Button size="sm" variant="ghost" onClick={() => unarchive(t)} data-testid={`unarchive-template-${t.id}`} className="h-8 text-white/70 hover:text-white hover:bg-white/10">
                    <ArchiveRestore className="w-3.5 h-3.5 mr-1.5"/> Restore
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => archive(t)} data-testid={`archive-template-${t.id}`} className="h-8 text-white/70 hover:text-white hover:bg-white/10">
                    <Archive className="w-3.5 h-3.5 mr-1.5"/> Archive
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => remove(t)} data-testid={`delete-template-${t.id}`} className="h-8 text-red-400 hover:bg-red-500/10 ml-auto">
                  <Trash2 className="w-3.5 h-3.5"/>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <TemplateEditor
        open={editor.open}
        setOpen={(v) => setEditor({ ...editor, open: v })}
        template={editor.template}
        onSaved={() => { setEditor({ open: false, template: null }); load(); }}
      />
      <NewFromTemplateDialog
        open={launch.open}
        setOpen={(v) => setLaunch({ ...launch, open: v })}
        template={launch.template}
        onCreated={() => { setLaunch({ open: false, template: null }); toast.success("Project created"); }}
      />
    </div>
  );
}
